import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { colors } from '@styleseat/brand';
import useUnmount from '../../../../hooks/useUnmount';

import { useSignupCountdownInfo } from './useSignupCountdownInfo';
import { SignupCountdownMainContent, SignupCountdownSideContent } from './SignupCountdown';

import styles from './SignupCountdown.module.scss';

const SHOWN_KEY = 'SignupCountdownContainerWasShown';

/**
 * Used for ensuring timer is only shown once per-session.
 */
export function markTimerShown() {
  if (window.sessionStorage) {
    sessionStorage.setItem(SHOWN_KEY, true);
  } else {
    // fallback for React Native
    window[SHOWN_KEY] = true;
  }
}

export function unmarkTimerShown() {
  if (window.sessionStorage) {
    sessionStorage.removeItem(SHOWN_KEY);
  } else {
    // fallback for React Native
    delete window[SHOWN_KEY];
  }
}

function wasTimerShown() {
  if (window.sessionStorage) {
    return !!sessionStorage.getItem(SHOWN_KEY);
  }
  return !!window[SHOWN_KEY];
}

export const SignupCountdownContainer = ({
  provider,
  openMs,
  closeMs,
}) => {
  const dispatch = useDispatch();
  const openSnackbar = props => dispatch.snackbar.open(props);
  const closeSnackbar = () => dispatch.snackbar.close();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const signupInfo = useSignupCountdownInfo(provider);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // Only show the timer once
    if (!signupInfo.showCountdown || wasTimerShown()) return () => {};
    const initialOpen = setTimeout(() => {
      openSnackbar({
        mainContent: <SignupCountdownMainContent signupCutoff={signupInfo.cutoff} />,
        sideContent: <SignupCountdownSideContent />,
        sideColor: colors.chill,
        className: styles.countdownContainer,
      });
      markTimerShown();
    }, openMs);

    const timeout = setTimeout(() => {
      closeSnackbar();
    }, closeMs);

    return () => {
      closeSnackbar();
      clearTimeout(initialOpen);
      clearTimeout(timeout);
    };
  }, [signupInfo.showCountdown]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useUnmount(() => {
    clearTimeout();
    closeSnackbar();
  });

  return <div />;
};

SignupCountdownContainer.propTypes = {
  provider: PropTypes.shape({
    creation_time: PropTypes.string,
    id: PropTypes.number,
  }),
  openMs: PropTypes.number,
  closeMs: PropTypes.number,
};

SignupCountdownContainer.defaultProps = {
  provider: null,
  openMs: 2000,
  closeMs: 10000,
};

export default SignupCountdownContainer;
